// Durable local storage for a session in progress.
//
// The contract with the UI is one line long and it is the whole point:
// **nothing is acknowledged on screen until it is in IndexedDB.** An observer
// who sees a tap register and then loses it has been lied to, and the cost is
// a contractor's afternoon.
//
// The contract with the server is the other half: rows are removed from here
// ONLY when the server reports them written, in the form they were sent. Never
// on a timeout, because a lost response may mean the write landed, and the
// retry resolves it safely because every row is idempotent on the server.

import { openDB, type IDBPDatabase } from "idb";
import type { Block, Tap } from "./tracker.ts";
import type { CategoryId } from "./categories.ts";
import { withLock } from "./serial.ts";
import { pruneConfirmed, pendingRows, type SentBatch } from "./prune.ts";

export interface QueuedNote {
  id: string;
  eventId: string | null;
  categoryId: CategoryId | null;
  notedAt: number;
  body: string;
}

export interface LocalSession {
  id: string;
  venueId: string;
  waveId: string;
  cleanerRef: string | null;
  startedAt: number;
  endedAt: number | null;
  /** Durable start intent; do not record or drain until the atomic header is confirmed. */
  startPending?: boolean;
  /** True only when this phone has recorded the full history from creation. */
  historyComplete?: boolean;
  /** Open blocks remain queued after acknowledgement so their close can upload. */
  pendingBlocks: Block[];
  acknowledgedOpenBlocks?: string[];
  pendingTaps: (Tap & { eventId: string })[];
  pendingNotes: QueuedNote[];
  lastSyncAt: number | null;
  lastError: string | null;
  /** Last actual upload attempt, retained across reopen for recovery details. */
  lastAttemptAt?: number;
  // Optional because records written before 2026-09-17 do not have them.
  /** Shown when offering to resume, so the observer knows which session. */
  venueName?: string;
  /** The next block number. Confirmed blocks leave the queue, so it cannot be
      recomputed from what is left, and reusing a number fails every batch. */
  nextSeq?: number;
  /** Last time the tracker was on screen. A session abandoned by a reload is
      closed here rather than at whatever time someone next opens the app. */
  lastSeenAt?: number;
  /** When the server was told the session ended. */
  endSyncedAt?: number | null;
  /** Whose session this is. A phone can be shared, and one person's queue must
      never be drained, resumed or shown under another person's sign-in. */
  observerId?: string;
  /** Every block this phone recorded for the session, open or closed, and never
      pruned. The queue above loses blocks as they upload, so a summary built from
      it after a resume counted only what was left. Local only: sync never reads
      it. Missing on records written before 2026-09-22. */
  history?: Block[];
  /** Who last asked the server whose session this is, and when, when it did
      not say. Per person: on a shared phone the server shows each person only
      their own sessions, so one person's "not mine" says nothing about another's. */
  ownerChecked?: { by: string; at: number };
}

const DB_NAME = "field-observation";
const STORE = "sessions";

let dbp: Promise<IDBPDatabase> | null = null;

function db() {
  if (!dbp) {
    const opening = openDB(DB_NAME, 1, {
      upgrade(d) {
        if (!d.objectStoreNames.contains(STORE)) d.createObjectStore(STORE, { keyPath: "id" });
      },
      // The browser closed the connection underneath us: storage pressure, or
      // iOS Safari dropping its IndexedDB server after the tab was backgrounded.
      // A cached dead connection made every later tap fail silently for the
      // rest of the shift, so forget it and reopen on the next call.
      terminated() { dbp = null; },
    });
    opening.catch(() => { dbp = null; });
    dbp = opening;
  }
  return dbp;
}

/** One retry with a fresh connection. Both operations here are idempotent. */
async function withDb<T>(op: (d: IDBPDatabase) => Promise<T>): Promise<T> {
  try {
    return await op(await db());
  } catch {
    dbp = null;
    return await op(await db());
  }
}

/**
 * Ask the browser to keep this data. Without it Safari evicts IndexedDB for
 * sites unused for seven days, and an observer working one shift a week fits
 * that profile exactly. Returns whether the browser agreed, which is worth
 * surfacing rather than assuming.
 */
export async function requestDurableStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

const changes = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("field-observation-queue");
changes?.addEventListener("message", () => dispatchEvent(new Event("field-observation-queue-remote")));
function changed() {
  dispatchEvent(new Event("field-observation-queue"));
  changes?.postMessage(null); // No records or credentials on the notification channel.
}

export async function saveSession(s: LocalSession): Promise<void> {
  await withDb((d) => d.put(STORE, s));
  changed();
}

export async function loadSession(id: string): Promise<LocalSession | undefined> {
  return withDb((d) => d.get(STORE, id));
}

export async function allSessions(): Promise<LocalSession[]> {
  return withDb((d) => d.getAll(STORE));
}

/** Select an existing intent or create one in the same transaction as its
 * lookup. The selector must check recording ownership before allocating IDs.
 * If an authorized put is already queued when ownership ends, the next tab's
 * transaction sees that durable intent and reuses it rather than allocating. */
export async function selectOrCreateSession(select: (sessions: LocalSession[]) => LocalSession): Promise<LocalSession> {
  const result = await withDb(async (d) => {
    const tx = d.transaction(STORE, "readwrite");
    try {
      const sessions = await tx.store.getAll();
      const session = select(sessions);
      const created = !sessions.some(s => s.id === session.id);
      if (created) await tx.store.put(session);
      await tx.done;
      return { session, created };
    } catch (e) {
      try { tx.abort(); } catch { /* Already aborted or committed. */ }
      await tx.done.catch(() => undefined);
      throw e;
    }
  });
  if (result.created) changed();
  return result.session;
}

// Every mutation of a session record is a read-modify-write, and there are two
// independent writers: the tap path in the UI and the sync path draining the
// queue. Both do `load -> compute -> save` with an await in the gap, so without
// serialisation the later save clobbers the earlier one.
//
// That is not a theoretical race. It was observed in a live run on 2026-09-10:
// `confirmApplied` loaded the session, a tap closed a block and saved, then
// `confirmApplied` saved its own older snapshot, in which the block was still
// open. The close was gone from the queue and the block stayed open in the
// database FOREVER, sitting behind a later block that had already closed.
// Nothing reported an error, because from each writer's point of view its own
// write succeeded.
//
// The promise chain orders this tab's callers; one IndexedDB readwrite
// transaction covers the read AND write so other tabs cannot interleave.

/**
 * The only safe way to change a session. Re-reads inside the lock, applies the
 * mutator to what is actually stored, and writes the result back before any
 * other mutation can interleave.
 */
export async function mutateSession(
  sessionId: string,
  fallback: LocalSession | null,
  mutate: (s: LocalSession) => LocalSession,
): Promise<LocalSession | undefined> {
  return withLock(sessionId, async () => {
    const next = await withDb(async (d) => {
      const tx = d.transaction(STORE, "readwrite");
      try {
        const current = (await tx.store.get(sessionId)) ?? fallback ?? undefined;
        const result = current ? mutate(current) : undefined;
        if (result) await tx.store.put(result);
        await tx.done;
        return result;
      } catch (e) {
        try { tx.abort(); } catch { /* Already aborted or committed. */ }
        await tx.done.catch(() => undefined);
        throw e;
      }
    });
    if (next) changed();
    return next;
  });
}

/**
 * The only place rows leave the queue. Removes exactly the rows the server now
 * holds in the form they were sent; see prune.ts for why "in the form they were
 * sent" is the part that matters.
 */
export async function confirmApplied(
  sessionId: string,
  sent: SentBatch,
  notConfirmed: Set<string>,
): Promise<LocalSession | undefined> {
  return mutateSession(sessionId, null, (s) => ({
    ...pruneConfirmed(s, sent, notConfirmed),
    lastSyncAt: Date.now(),
    lastError: null,
  }));
}

export async function recordSyncError(sessionId: string, message: string): Promise<void> {
  await mutateSession(sessionId, null, (s) => ({ ...s, lastError: message }));
}

/** Everything still waiting, across all sessions. */
export async function pendingCount(): Promise<number> {
  const all = await allSessions();
  return all.reduce((acc, s) => acc + pendingRows(s), 0);
}
